import { Link, useLocation } from 'react-router';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import { cn } from '@rocco/utils/cn';

import { MOBILE_SECTION_TITLE } from '~/ui-rocco/pages/directory-browse/mobile-section-title';
import { emptyResultsClearTo, searchTermOf } from './DirectoryEmptyResults';

interface BrowseNoResultsProps {
    resultsType: 'products' | 'suppliers';
    /** Plural noun the empty state names the listing by. */
    countNoun: string;
}

/**
 * The revamp browse grid's empty state below md, set in the browse page's own
 * type: the section-title heading, the intro's grey caption, and the Get
 * Matched button shape. Says whether a search or the filters emptied the
 * listing, and offers the one action that undoes it. Desktop keeps the legacy
 * section until its own design moves.
 */
export const BrowseNoResults = ({
    resultsType,
    countNoun,
}: BrowseNoResultsProps) => {
    const { pathname, search } = useLocation();
    const term = searchTermOf(search);

    return (
        <>
            <div
                className="flex flex-col items-start gap-3 px-4 pt-10 pb-12 md:hidden"
                data-testid="browse-no-results"
            >
                <h2
                    className={cn(
                        'm-0 [overflow-wrap:anywhere] text-[#323232]',
                        MOBILE_SECTION_TITLE,
                    )}
                >
                    {term
                        ? `No ${countNoun} match “${term}”`
                        : `No ${countNoun} match those filters`}
                </h2>
                <p className="m-0 text-[0.9375rem] leading-[1.3] font-medium tracking-[-0.02em] text-[#767676]">
                    {term
                        ? `Try a different word, or browse all ${countNoun} in this category.`
                        : 'Try widening one of them, or start again with the whole category.'}
                </p>
                <Link
                    to={emptyResultsClearTo(pathname, search)}
                    preventScrollReset
                    className="mt-3 flex h-[3.25rem] items-center justify-center rounded-[1.375rem] bg-[#323232] px-[1.625rem] text-[1rem] leading-[1.3] font-medium tracking-[0.02rem] text-white no-underline"
                >
                    {term ? 'Clear search' : 'Clear filters'}
                </Link>
            </div>
            <div className="max-md:hidden">
                <NoResultsSection resultsType={resultsType} />
            </div>
        </>
    );
};
